import React, { useCallback, useEffect, useState } from 'react';

interface AdminGateProps {
  children: React.ReactNode;
}

export default function AdminGate({ children }: AdminGateProps) {
  const [status, setStatus] = useState<'loading' | 'authed' | 'anon'>('loading');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const checkSession = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/admin-auth-session', {
        credentials: 'include',
      });
      if (res.ok) {
        setStatus('authed');
      } else {
        setStatus('anon');
      }
    } catch {
      setStatus('anon');
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/admin-auth-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };

      if (!res.ok) {
        setError(data.message || data.error || 'ログインに失敗しました');
        return;
      }
      setPassword('');
      setStatus('authed');
    } catch {
      setError('通信に失敗しました。Netlify 上で開いているか確認してください。');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-[#6B7280] text-sm">確認中…</p>
      </div>
    );
  }

  if (status === 'anon') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <h1 className="text-xl font-bold text-[#111827] mb-1">管理者ログイン</h1>
          <p className="text-sm text-[#6B7280] mb-6">記事管理（CMS）に入ります</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1">
                パスワード
              </label>
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                required
              />
            </div>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-[#02D0F0] hover:bg-[#07DBCA] text-white font-medium py-2.5 disabled:opacity-60"
            >
              {submitting ? '送信中…' : 'ログイン'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
